import { useEffect, useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@paulownia/shared/components/ui/button';
import { Badge } from '@paulownia/shared/components/ui/badge';
import { Plus, ChevronDown, ChevronRight, GripVertical, Pencil, Ban, Check, Eye, EyeOff } from 'lucide-react';
import { listProducts, updateProduct, createProduct, deleteProductById, updateProductsOrder, setProductOutOfStock, setProductActive } from '@paulownia/shared/services/products';
import { invalidateProductsCache } from '@paulownia/shared/services/products-cache';
import { getTenantById } from '@paulownia/shared/services/tenant';
import { uploadProductImage, updateProductImageUrls, type ProcessedImages } from '@paulownia/shared';
import { type Product, type ProductOption, type Market, type TenantMarket, isProductOutOfStock, isProductActive } from '@paulownia/shared/types';
import { getTenantMarketsWithDetails } from '@paulownia/shared/services/markets';
import { toast } from 'sonner';
import { ProductEditDialog } from './ProductEditDialog';
import { ProductWizard } from './components/ProductWizard';
import { cn } from '@paulownia/shared/lib/utils';
import { useTenant } from '@paulownia/shared/lib/tenant-context';

// Helper to capitalize first letter
function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Sortable Product Card Component
function SortableProductCard({
  product,
  onEdit,
  onToggleOutOfStock,
  onToggleActive,
}: {
  product: Product;
  onEdit: (product: Product) => void;
  onToggleOutOfStock: (product: Product) => void;
  onToggleActive: (product: Product) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const outOfStock = isProductOutOfStock(product);
  const active = isProductActive(product);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex flex-col bg-white border rounded-xl p-4 transition-all',
        'hover:shadow-md hover:border-gray-300',
        isDragging && 'shadow-lg border-blue-300 bg-blue-50/50 z-50',
        !active && 'bg-gray-100 border-gray-300 opacity-60',
        active && outOfStock && 'bg-red-50/50 border-red-200'
      )}
    >
      {/* Top Row: Drag Handle + Name + Stock Toggle + Edit */}
      <div className="flex items-start gap-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className={cn(
            'flex-shrink-0 cursor-grab active:cursor-grabbing p-1 rounded touch-none',
            'opacity-40 group-hover:opacity-100 transition-opacity',
            'hover:bg-gray-100',
            isDragging && 'opacity-100'
          )}
        >
          <GripVertical className="h-5 w-5 text-gray-400" />
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={cn(
              'font-semibold truncate',
              outOfStock ? 'text-gray-500' : 'text-gray-900'
            )}>
              {product.shortenName || product.name}
            </h3>
            {!active && (
              <Badge className="text-xs bg-gray-500 text-white">
                Inactif
              </Badge>
            )}
            {active && outOfStock && (
              <Badge className="text-xs bg-red-500 text-white">
                Rupture
              </Badge>
            )}
          </div>
        </div>

        {/* Active Toggle Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleActive(product);
          }}
          className={cn(
            'flex-shrink-0 p-2 rounded-lg transition-all',
            active
              ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              : 'text-green-600 hover:bg-green-50',
            'opacity-40 group-hover:opacity-100',
            isDragging && 'opacity-0'
          )}
          title={active ? 'Désactiver le produit' : 'Activer le produit'}
        >
          {active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>

        {/* Stock Toggle Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleOutOfStock(product);
          }}
          className={cn(
            'flex-shrink-0 p-2 rounded-lg transition-all',
            outOfStock
              ? 'text-green-600 hover:bg-green-50'
              : 'text-red-400 hover:text-red-600 hover:bg-red-50',
            'opacity-40 group-hover:opacity-100',
            isDragging && 'opacity-0'
          )}
          title={outOfStock ? 'Remettre en stock' : 'Marquer en rupture'}
        >
          {outOfStock ? <Check className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
        </button>

        {/* Edit Button */}
        <button
          onClick={() => onEdit(product)}
          className={cn(
            'flex-shrink-0 p-2 rounded-lg transition-all',
            'text-gray-400 hover:text-blue-600 hover:bg-blue-50',
            'opacity-40 group-hover:opacity-100',
            isDragging && 'opacity-0'
          )}
          title="Modifier"
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>

      {/* Bottom Row: Price + Options */}
      <div className="mt-3 pl-9 flex flex-wrap items-center gap-2">
        {/* Price */}
        {product.price !== undefined ? (
          <span className="font-bold text-green-600">
            {product.price.toFixed(2)}€
            <span className="text-xs font-normal text-gray-400 ml-1">
              /{(product.saleMode === 'UNIT' || product.saleMode === 'UNIT_PRICED_BY_WEIGHT') ? 'pce' : 'kg'}
            </span>
          </span>
        ) : (
          <span className="text-sm text-gray-400 italic">Prix non défini</span>
        )}

        {/* Available Options Badges */}
        {product.availableOptions && product.availableOptions.length > 0 && (
          <div className="flex flex-wrap gap-1 ml-2">
            {product.availableOptions.map((opt) => (
              <span
                key={opt.id}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
              >
                {opt.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// SubSection Group for products with the same subSection
function SubSectionGroup({
  subSectionName,
  products,
  onEdit,
  onToggleOutOfStock,
  onToggleActive,
  onReorder,
}: {
  subSectionName: string | null;
  products: Product[];
  onEdit: (product: Product) => void;
  onToggleOutOfStock: (product: Product) => void;
  onToggleActive: (product: Product) => void;
  onReorder: (groupProducts: Product[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = products.findIndex((p) => p.id === active.id);
      const newIndex = products.findIndex((p) => p.id === over.id);
      const reordered = arrayMove(products, oldIndex, newIndex);
      onReorder(reordered);
    }
  };

  return (
    <div className="space-y-2">
      {subSectionName && (
        <h3 className="text-base font-semibold text-gray-700 pl-1">
          {capitalize(subSectionName)}
        </h3>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={products.map((p) => p.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="grid gap-2 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((product) => (
              <SortableProductCard
                key={product.id}
                product={product}
                onEdit={onEdit}
                onToggleOutOfStock={onToggleOutOfStock}
                onToggleActive={onToggleActive}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

// Group Section Component - groups products by section with subsection support
function GroupSection({
  sectionName,
  productGroups,
  onEdit,
  onToggleOutOfStock,
  onToggleActive,
  onReorder,
}: {
  sectionName: string;
  productGroups: { subSection: string | null; products: Product[] }[];
  onEdit: (product: Product) => void;
  onToggleOutOfStock: (product: Product) => void;
  onToggleActive: (product: Product) => void;
  onReorder: (sectionProducts: Product[]) => void;
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Calculate total products in the section
  const totalProducts = productGroups.reduce((sum, g) => sum + g.products.length, 0);

  // Handle reorder within a subsection group
  const handleSubSectionReorder = (subSection: string | null, reorderedProducts: Product[]) => {
    // Rebuild the full section products list maintaining the subsection order
    const newSectionProducts: Product[] = [];
    productGroups.forEach((group) => {
      if (group.subSection === subSection) {
        newSectionProducts.push(...reorderedProducts);
      } else {
        newSectionProducts.push(...group.products);
      }
    });
    onReorder(newSectionProducts);
  };

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center gap-2 w-full">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center gap-2 flex-1 text-left group"
        >
          <span className="text-gray-400 group-hover:text-gray-600 transition-colors">
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </span>
          <h2 className="font-bold text-lg text-gray-900">
            {capitalize(sectionName)}
          </h2>
          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
            {totalProducts}
          </span>
        </button>
      </div>

      {/* Products by SubSection */}
      {!isCollapsed && (
        <div className="space-y-6 pl-2">
          {productGroups.map((group, index) => (
            <SubSectionGroup
              key={group.subSection || `no-subsection-${index}`}
              subSectionName={group.subSection}
              products={group.products}
              onEdit={onEdit}
              onToggleOutOfStock={onToggleOutOfStock}
              onToggleActive={onToggleActive}
              onReorder={(reordered) => handleSubSectionReorder(group.subSection, reordered)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ManageProducts() {
  const { currentTenantId } = useTenant();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [tenantSettings, setTenantSettings] = useState<{
    sectionOrder?: Record<string, number>;
    subSectionOrder?: Record<string, Record<string, number>>;
  } | null>(null);
  const [tenantMarkets, setTenantMarkets] = useState<(TenantMarket & { market: Market })[]>([]);

  const refresh = async () => {
    if (!currentTenantId) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const ps = await listProducts(currentTenantId);
      setProducts(ps);
    } catch (err) {
      console.error('Error loading products:', err);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId]);

  // Load tenant settings for section ordering
  useEffect(() => {
    if (!currentTenantId) {
      setTenantSettings(null);
      return;
    }

    getTenantById(currentTenantId)
      .then((tenant) => {
        if (tenant) {
          setTenantSettings(tenant.settings || null);
        }
      })
      .catch((err) => {
        console.error('Error loading tenant settings:', err);
        // Don't show error toast for settings - it's not critical
      });
  }, [currentTenantId]);

  // Load tenant markets for product availability
  useEffect(() => {
    if (!currentTenantId) {
      setTenantMarkets([]);
      return;
    }

    getTenantMarketsWithDetails(currentTenantId)
      .then((markets) => {
        setTenantMarkets(markets.sort((a, b) => a.market.dayOfWeek - b.market.dayOfWeek));
      })
      .catch((err) => {
        console.error('Error loading tenant markets:', err);
      });
  }, [currentTenantId]);

  // Get unique sections for the CreatableSelect
  const sections = useMemo(() => {
    const secs = new Set(products.map((p) => p.section).filter(Boolean) as string[]);
    return Array.from(secs).sort();
  }, [products]);

  // Get subSections grouped by section (sorted by tenant settings or alphabetically)
  const subSectionsBySection = useMemo(() => {
    const result: Record<string, string[]> = {};
    products.forEach((p) => {
      const section = p.section || 'Sans section';
      if (p.subSection) {
        if (!result[section]) result[section] = [];
        if (!result[section].includes(p.subSection)) {
          result[section].push(p.subSection);
        }
      }
    });
    // Sort subSections using tenant settings or alphabetically
    Object.keys(result).forEach((section) => {
      if (tenantSettings?.subSectionOrder?.[section]) {
        result[section].sort((a, b) => {
          const orderA = tenantSettings.subSectionOrder![section][a] ?? 999;
          const orderB = tenantSettings.subSectionOrder![section][b] ?? 999;
          return orderA - orderB;
        });
      } else {
        result[section].sort();
      }
    });
    return result;
  }, [products, tenantSettings]);

  // Get all unique options from all products (with merged configuration)
  // Excludes 'composition' type options since they are managed in a separate section
  const existingOptions = useMemo(() => {
    const optMap = new Map<string, ProductOption>();
    products.forEach((p) => {
      p.availableOptions?.forEach((opt) => {
        // Skip composition options - they are managed separately
        if (opt.type === 'composition') return;
        const existing = optMap.get(opt.label);
        if (!existing) {
          // First time seeing this option - add it
          optMap.set(opt.label, { ...opt, choices: opt.choices ? [...opt.choices] : undefined });
        } else {
          // Merge: prefer select/multiselect over boolean
          if (existing.type === 'boolean' && opt.type !== 'boolean') {
            optMap.set(opt.label, { ...opt, choices: opt.choices ? [...opt.choices] : undefined });
          } else if (opt.choices && opt.choices.length > 0) {
            // Merge choices from all products (deduplicate by label)
            const existingChoices = existing.choices || [];
            const existingLabels = new Set(existingChoices.map(c => c.label));
            const newChoices = opt.choices.filter(c => !existingLabels.has(c.label));
            if (newChoices.length > 0) {
              optMap.set(opt.label, {
                ...existing,
                choices: [...existingChoices, ...newChoices],
              });
            }
          }
        }
      });
    });
    return Array.from(optMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [products]);

  // Group products by section, sorted by displayOrder
  const productsBySection = useMemo(() => {
    const grouped: Record<string, Product[]> = {};

    products.forEach((product) => {
      const sec = product.section || 'Sans section';
      if (!grouped[sec]) grouped[sec] = [];
      grouped[sec].push(product);
    });

    // Sort products within each section by displayOrder
    Object.keys(grouped).forEach((sec) => {
      grouped[sec].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
    });

    return grouped;
  }, [products]);

  const sortedSections = useMemo(() => {
    const sectionKeys = Object.keys(productsBySection);
    // Use tenant settings sectionOrder if available, otherwise fallback to displayOrder
    if (tenantSettings?.sectionOrder) {
      return sectionKeys.sort((a, b) => {
        const orderA = tenantSettings.sectionOrder![a] ?? 999;
        const orderB = tenantSettings.sectionOrder![b] ?? 999;
        return orderA - orderB;
      });
    }
    // Fallback: Sort sections by the minimum displayOrder of products in each section
    return sectionKeys.sort((a, b) => {
      const minA = Math.min(...productsBySection[a].map(p => p.displayOrder ?? 999));
      const minB = Math.min(...productsBySection[b].map(p => p.displayOrder ?? 999));
      return minA - minB;
    });
  }, [productsBySection, tenantSettings]);

  // Group products by subsection within each section (like customer page)
  const productsBySubSection = useMemo(() => {
    const result: Record<string, { subSection: string | null; products: Product[] }[]> = {};

    Object.entries(productsBySection).forEach(([section, sectionProducts]) => {
      const subSections = subSectionsBySection[section] || [];

      if (subSections.length === 0) {
        // No subsections, just return all products in one group
        result[section] = [{ subSection: null, products: sectionProducts }];
      } else {
        // Group products by subsection
        const groups: { subSection: string | null; products: Product[] }[] = [];

        // First, products without subsection
        const productsWithoutSubSection = sectionProducts.filter(p => !p.subSection);
        if (productsWithoutSubSection.length > 0) {
          groups.push({ subSection: null, products: productsWithoutSubSection });
        }

        // Then, products grouped by subsection (in order)
        subSections.forEach(subSection => {
          const subSectionProducts = sectionProducts.filter(p => p.subSection === subSection);
          if (subSectionProducts.length > 0) {
            groups.push({ subSection, products: subSectionProducts });
          }
        });

        result[section] = groups;
      }
    });

    return result;
  }, [productsBySection, subSectionsBySection]);

  // Handle opening edit dialog
  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsCreating(false);
    setIsDialogOpen(true);
  };

  // Handle creating new product - always use the new wizard
  const handleCreate = () => {
    setIsWizardOpen(true);
  };

  // Handle save from wizard (always creates new product)
  const handleWizardSave = async (product: Product, pendingImage: ProcessedImages | null) => {
    if (!currentTenantId) {
      toast.error('Tenant non sélectionné');
      return;
    }

    try {
      // Find highest displayOrder in the section
      const sectionName = product.section || 'Sans section';
      const sectionProducts = productsBySection[sectionName] || [];
      const maxOrder = sectionProducts.reduce((max, p) =>
        Math.max(max, p.displayOrder ?? 0), 0
      );

      const createdProduct = await createProduct({
        ...product,
        displayOrder: maxOrder + 1,
        tenantId: currentTenantId,
      });

      // If there's a pending image, upload it now that we have the product ID
      if (pendingImage) {
        try {
          const { imageUrl, thumbnailUrl } = await uploadProductImage(
            currentTenantId,
            createdProduct.id,
            pendingImage
          );
          await updateProductImageUrls(createdProduct.id, imageUrl, thumbnailUrl);
        } catch (imageErr) {
          console.error('Error uploading image:', imageErr);
          // Don't fail the whole operation - product was created successfully
          toast.error("Produit créé mais l'image n'a pas pu être téléchargée");
        }
      }

      invalidateProductsCache(currentTenantId);
      toast.success('Produit créé');
      await refresh();
    } catch (err) {
      console.error('Error creating product:', err);
      const error = err as { code?: string; message?: string };

      if (error.code === 'permission-denied') {
        toast.error('Permissions insuffisantes. Veuillez vous reconnecter.');
      } else if (error.code === 'unauthenticated') {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else if (error.message?.includes('non authentifié')) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        const message = err instanceof Error ? err.message : 'Erreur lors de la création';
        toast.error(message);
      }
      throw err;
    }
  };

  // Handle save (create or update)
  const handleSave = async (product: Product) => {
    try {
      if (!currentTenantId) {
        toast.error('Tenant non sélectionné');
        return;
      }
      if (isCreating) {
        // Find highest displayOrder in the section
        const sectionName = product.section || 'Sans section';
        const sectionProducts = productsBySection[sectionName] || [];
        const maxOrder = sectionProducts.reduce((max, p) =>
          Math.max(max, p.displayOrder ?? 0), 0
        );

        await createProduct({
          ...product,
          displayOrder: maxOrder + 1,
          tenantId: currentTenantId,
        });
        invalidateProductsCache(currentTenantId);
        toast.success('Produit créé');
      } else {
        await updateProduct(product);
        invalidateProductsCache(currentTenantId);
        toast.success('Produit mis à jour');
      }
      await refresh();
    } catch (err) {
      console.error('Error saving product:', err);
      const error = err as { code?: string; message?: string };

      // Handle specific Firebase errors
      if (error.code === 'permission-denied') {
        toast.error('Permissions insuffisantes. Veuillez vous reconnecter.');
      } else if (error.code === 'unauthenticated') {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else if (error.message?.includes('non authentifié')) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        const message = err instanceof Error ? err.message : 'Erreur lors de la sauvegarde';
        toast.error(message);
      }
      throw err;
    }
  };

  // Handle delete
  const handleDelete = async (productId: string) => {
    try {
      await deleteProductById(productId);
      invalidateProductsCache(currentTenantId ?? undefined);
      toast.success('Produit supprimé');
      await refresh();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(message);
      throw err;
    }
  };

  // Handle reorder within section
  const handleReorder = async (_section: string, reorderedProducts: Product[]) => {
    // Optimistically update the UI
    setProducts((prev) => {
      const updated = [...prev];
      reorderedProducts.forEach((product, index) => {
        const idx = updated.findIndex((p) => p.id === product.id);
        if (idx !== -1) {
          updated[idx] = { ...updated[idx], displayOrder: index };
        }
      });
      return updated;
    });

    // Persist to database
    try {
      const updates = reorderedProducts.map((product, index) => ({
        id: product.id,
        displayOrder: index,
      }));
      await updateProductsOrder(updates);
      invalidateProductsCache(currentTenantId ?? undefined);
    } catch (err) {
      console.error('Error saving order:', err);
      const error = err as { code?: string; message?: string };

      // Handle specific Firebase errors
      if (error.code === 'permission-denied' || error.code === 'unauthenticated' || error.message?.includes('non authentifié')) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        toast.error('Erreur lors de la sauvegarde de l\'ordre');
      }
      await refresh(); // Revert on error
    }
  };

  // Handle toggling out-of-stock status
  const handleToggleOutOfStock = async (product: Product) => {
    const currentlyOutOfStock = isProductOutOfStock(product);
    const newOutOfStockState = !currentlyOutOfStock;

    // Optimistically update UI
    setProducts((prev) =>
      prev.map((p) =>
        p.id === product.id
          ? { ...p, outOfStockDate: newOutOfStockState ? new Date().toISOString().split('T')[0] : undefined }
          : p
      )
    );

    try {
      await setProductOutOfStock(product.id, newOutOfStockState);
      invalidateProductsCache(currentTenantId || undefined);
      toast.success(newOutOfStockState ? 'Produit marqué en rupture' : 'Produit remis en stock');
    } catch (err) {
      console.error('Error toggling out-of-stock:', err);
      const error = err as { code?: string };
      // Revert on error
      await refresh();
      if (error.code === 'permission-denied' || error.code === 'unauthenticated') {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        toast.error('Erreur lors de la mise à jour du stock');
      }
    }
  };

  // Handle toggling active status (seasonal products)
  const handleToggleActive = async (product: Product) => {
    const currentlyActive = isProductActive(product);
    const newActiveState = !currentlyActive;

    // Optimistically update UI
    setProducts((prev) =>
      prev.map((p) =>
        p.id === product.id
          ? { ...p, isActive: newActiveState }
          : p
      )
    );

    try {
      await setProductActive(product.id, newActiveState);
      invalidateProductsCache(currentTenantId || undefined);
      toast.success(newActiveState ? 'Produit activé' : 'Produit désactivé');
    } catch (err) {
      console.error('Error toggling active status:', err);
      const error = err as { code?: string };
      // Revert on error
      await refresh();
      if (error.code === 'permission-denied' || error.code === 'unauthenticated') {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        toast.error('Erreur lors de la mise à jour du statut');
      }
    }
  };

  return (
    <div className="pb-20 p-4 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900">Produits</h1>

        <Button onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Nouveau produit</span>
        </Button>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-500">
        Produits groupés par section et sous-section (même structure que l'affichage client)
      </p>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <p className="text-gray-500 mb-4">Aucun produit</p>
          <Button onClick={handleCreate} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Créer votre premier produit
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {sortedSections.map((sectionName) => (
            <GroupSection
              key={sectionName}
              sectionName={sectionName}
              productGroups={productsBySubSection[sectionName] || []}
              onEdit={handleEdit}
              onToggleOutOfStock={handleToggleOutOfStock}
              onToggleActive={handleToggleActive}
              onReorder={(reordered) => handleReorder(sectionName, reordered)}
            />
          ))}
        </div>
      )}

      {/* Edit/Create Dialog (legacy) */}
      <ProductEditDialog
        product={editingProduct}
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditingProduct(null);
          setIsCreating(false);
        }}
        onSave={handleSave}
        onDelete={handleDelete}
        sections={sections}
        subSectionsBySection={subSectionsBySection}
        existingOptions={existingOptions}
        allProducts={products}
        isNew={isCreating}
        tenantId={currentTenantId || undefined}
        tenantMarkets={tenantMarkets}
      />

      {/* Product Wizard (new product flow) */}
      <ProductWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onSave={handleWizardSave}
        sections={sections}
        subSectionsBySection={subSectionsBySection}
        existingOptions={existingOptions}
        allProducts={products}
      />
    </div>
  );
}
